import {
  Button,
  Input,
  Modal,
  RadioButton,
  Select,
} from '@equitymultiple/react-eui';
import { graphql, Link, navigate, useStaticQuery } from 'gatsby';
import React, { FormEventHandler, useRef, useState } from 'react';

import Accordion from '../../../../components/Accordion/Accordion';
import SvgProjector from '../../../../components/SvgProjector/SvgProjector';
import useScrollPosition from '../../../../hooks/useVerticalScrollPosition';
import * as styles from './BlogFilter.module.scss';
import { BlogFilterProps, FilterOptions } from './BlogFilter.types';

type FiltersData = {
  type: FilterOptions;
  title: string;
  options: {
    name: string;
    path: string;
    radioGroupName: 'sort' | 'category' | 'topic';
  }[];
}[];

export const BlogFilter = ({
  searchBarProps,
  defaultExpandedAccordion,
  selectedOptionCopy,
  routeToBlogOnSearch,
}: BlogFilterProps) => {
  const { allWpTag: rawTagData, allWpCategory } = useStaticQuery(graphql`
    query BlogFilterQuery {
      allWpTag(filter: { count: { gte: 1 } }) {
        nodes {
          name
          slug
          count
        }
      }
      allWpCategory(
        sort: { count: DESC }
        filter: { count: { gte: 3 }, slug: { ne: "investor-stories" } }
      ) {
        nodes {
          name
          link
        }
      }
    }
  `);

  // we're using the tag's slug to reference the tag
  const tagsThatArentTopics = ['popular-post', 'editor-pick', 'featured'];

  const allWpTag = rawTagData.nodes.filter((tag: { slug: string }) => {
    return !tagsThatArentTopics.includes(tag.slug);
  });

  const [selectedOption, setSelectedOption] = useState<{
    type: FilterOptions | '';
    path: string;
  }>({ type: '', path: '' });
  const [openModalIndex, setOpenModalIndex] = useState(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { scrollDirection } = useScrollPosition();

  const filtersData: FiltersData = [
    {
      type: 'most-recent' || 'most-popular',
      title: 'Sort by:',
      options: [
        {
          name: 'Most Recent',
          path: '/blog/most-recent',
          radioGroupName: 'sort',
        },
        // TODO: uncomment when Most Popular filter is added to the project
        // { name: 'Most Popular', path: '/blog/most-popular', radioGroupName },
      ],
    },
    {
      type: 'category',
      title: 'Filter by category:',
      options: allWpCategory.nodes.map(
        (category: { name: string; link: string }) => {
          return {
            name: category.name,
            path: category.link,
            radioGroupName: 'category',
          };
        },
      ),
    },
    {
      type: 'topic',
      title: 'Filter by topic:',
      options: allWpTag.map(
        ({ name, slug }: { name: string; slug: string }) => {
          return {
            name: name,
            path: `/blog/topics/${slug}`,
            radioGroupName: 'topic',
          };
        },
      ),
    },
  ];

  const SelectedOption = () => {
    return (
      <div className={styles.selectedOption}>
        {selectedOptionCopy}

        <Link to="/blog" className={styles.clearSelectedOption}>
          <SvgProjector fileUrl="icons/close" />
        </Link>
      </div>
    );
  };

  const isDefaultExpandedAccordion = (type: FilterOptions) => {
    return defaultExpandedAccordion === type;
  };

  const renderFilters = () => {
    return filtersData.map(({ options, type, title }, index) => {
      return (
        <>
          {options && (
            <Accordion
              variant="slim"
              defaultExpanded={isDefaultExpandedAccordion(type)}
              summary={title}
              key={title}
              className={
                index !== filtersData.length - 1 ? styles.blogFilterBorder : ''
              }
            >
              {isDefaultExpandedAccordion(type) ? (
                <SelectedOption />
              ) : (
                options.map((option) => {
                  const optionReference = option.name.split(' ').join('');
                  return (
                    <button
                      type="button"
                      key={option.path}
                      disabled={selectedOption.path === option.path}
                      onClick={() => {
                        setSelectedOption({ type, path: option.path });
                      }}
                      className={styles.filterOption}
                    >
                      <RadioButton
                        checked={selectedOption.path === option.path}
                        name={option.radioGroupName}
                        id={optionReference}
                        label={option.name}
                        onChange={() => {
                          setSelectedOption({ type, path: option.path });
                        }}
                        key={option.path}
                      />
                    </button>
                  );
                })
              )}
              {!isDefaultExpandedAccordion(type) && (
                <Button
                  disabled={selectedOption.type !== type}
                  className={styles.applyFilterButton}
                  href={selectedOption.path}
                >
                  Apply
                </Button>
              )}
            </Accordion>
          )}
        </>
      );
    });
  };

  const getModalBody = () => {
    return (
      <div className={styles.modalBody}>
        {filtersData.map((filter, index) => {
          return (
            <>
              {filter.options && (
                <div
                  className={styles.modalFilterOption}
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${filter.options[0].name}-${index}`}
                >
                  <Select
                    id={filter.title}
                    onChange={(value: string) =>
                      setSelectedOption({
                        type: filter.type,
                        path: value,
                      })
                    }
                    value={selectedOption.path}
                    label={filter.title}
                    options={filter.options.map((option) => ({
                      label: option.name,
                      value: option.path,
                    }))}
                  />
                </div>
              )}
            </>
          );
        })}
        <div className={styles.modalButtonContainer}>
          <Button
            disabled={selectedOption.path === ''}
            href={selectedOption.path}
            className={styles.modalFilterButton}
          >
            Apply
          </Button>
          <Button
            onClick={() => setSelectedOption({ type: '', path: '' })}
            variant="outlined"
            className={styles.modalFilterButton}
          >
            Clear
          </Button>
        </div>
      </div>
    );
  };

  const submitForm: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    if (routeToBlogOnSearch) {
      navigate(`/blog?search=${searchBarProps.inputValue}`);
    } else {
      searchBarProps.setQuery(searchBarProps.inputValue);
    }
  };

  return (
    <div className={styles.blogFilter}>
      {/* DESKTOP */}
      <div className="visibleXlOrGreater">
        {searchBarProps.query && searchBarProps.resultsCountDisplay}
        <form onSubmit={submitForm} className={styles.searchForm}>
          <Input
            className={styles.searchField}
            label="Search Articles"
            value={searchBarProps.inputValue}
            ref={inputRef}
            type="text"
            id="desktopSearchInput"
            data-testid="searchInput"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              searchBarProps.setInputValue(e.target.value)
            }
            onClear={() => searchBarProps.setQuery('')}
          />
          <Button
            type="submit"
            size="large"
            trailingIcon="search"
            label="search"
          />
        </form>
        <div className={styles.inputBorder} />
        {renderFilters()}
      </div>

      {/* MOBILE */}
      <div
        data-testid="mobileFilter"
        className={`${styles.mobileFilter} ${
          scrollDirection === 'down'
            ? styles.mobileFilterHidden
            : styles.mobileFilterRevealed
        } visibleLgOrLesser`}
      >
        <Modal onClose={() => setOpenModalIndex(null)} open={openModalIndex}>
          {getModalBody()}
        </Modal>
        <div className="container">
          <div className={styles.mobileSearch}>
            <form onSubmit={submitForm}>
              <Input
                className={styles.searchField}
                label="Search Articles"
                ref={inputRef}
                id="mobileSearchInput"
                type="text"
                data-testid="mobileInput"
                value={searchBarProps.inputValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  searchBarProps.setInputValue(e.target.value)
                }
                onClear={() => searchBarProps.setQuery('')}
              />
              <Button
                type="submit"
                size="large"
                trailingIcon="search"
                label="search"
              />
            </form>
            <Button
              leadingIcon="filter"
              size="large"
              onClick={() => setOpenModalIndex(1)}
              label="Filter Articles"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
